import {
  IDockviewPanelProps,
} from "dockview-react";
import React, { useState } from "react";
import { api } from "./utils/api";
import { IoTrashBinSharp } from "react-icons/io5";
import SafeNumberInput from "./SafeNumberInput";
import { MdEdit } from "react-icons/md";
import { MdHideImage } from "react-icons/md";
import { ObservationEditor } from "./Editor";

interface IObservation {
  id: number;
  description: string;
  content: string;
  date: string | null;
}

interface IDataRequest {
  page: number;
  query: string;
  start_date: string;
  end_date: string;
}

interface IEditorState {
  id?: number;
  description?: string;
  content?: string;
  date?: string;
}

export function BRSNLibrary(props: IDockviewPanelProps) {
  const [observations, setObservations] = useState<IObservation[]>([]);
  const [requestParams, setRequestParams] = useState<IDataRequest>({
    page: 1,
    query: "",
    start_date: "",
    end_date: "",
  });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [globalHiddenImgs, setGlobalHiddenImgs] = React.useState<boolean>(true);
  const [editorState, setEditorState] = React.useState<IEditorState | null>(null);

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  async function submit() {
    if (requestParams.start_date && !dateRegex.test(requestParams.start_date)) {
      setErrorMessage("Start date must be in YYYY-MM-DD format");
      return;
    }
    if (requestParams.end_date && !dateRegex.test(requestParams.end_date)) {
      setErrorMessage("End date must be in YYYY-MM-DD format");
      return;
    }
    setErrorMessage("");
    setIsLoading(true);

    try {
      const resp = await api.get<IObservation[]>(`/observations`, {
        params: requestParams,
      });
      setObservations(resp.data);
      console.log(resp.data);
    } catch (error: any) {
      console.error(error?.message ?? error);
      setErrorMessage("Failed to load observations");
    } finally {
      setIsLoading(false);
    }
  }

  async function deleteObservation(id: number) {
    // confirm
    if (!window.confirm("Are you sure you want to delete this observation?")) {
      return;
    }

    setErrorMessage("");
    setIsLoading(true);

    try {
      await api.delete(`/observation/${id}`);
      setObservations((obs) => obs.filter((o) => o.id !== id));
    } catch (error: any) {
      console.error(error?.message ?? error);
      setErrorMessage("Failed to delete observation");
    } finally {
      setIsLoading(false);
    }
  }


  if (isLoading) {
    return <div>Loading…</div>;
  }

  if (editorState !== null) {
    return (
      <ObservationEditor
        id={editorState.id}
        initialDescription={editorState.description ?? ""}
        initialContent={editorState.content ?? ""}
        initialDate={editorState.date ?? ""}
        onClose={() => setEditorState(null)}
      />
    );
  }

  return (
    <div className="h-full w-full p-4 pt-0 overflow-scroll">
      {errorMessage && <div className="text-red-500">{errorMessage}</div>}
      <div className="flex justify-between items-center">

        <div className="m-2">
          <MdHideImage
            size={25}
            className="inline cursor-pointer"
            onClick={() => {
              setGlobalHiddenImgs(!globalHiddenImgs);
              console.log("globalHiddenImgs ", globalHiddenImgs);
            }}
          />
        </div>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex flex-row gap-4 mb-4 items-center"
      >
        <label htmlFor="page">
          Page:
          <SafeNumberInput
            id="page"
            value={requestParams.page}
            onChange={(value) => {
              setRequestParams({
                ...requestParams,
                page: value,
              });
            }}
          />
        </label>

        <label htmlFor="query">
          Query:
          <input
            placeholder="INTC"
            type="text"
            id="query"
            name="query"
            value={requestParams.query}
            onChange={(e) => {
              setRequestParams({
                ...requestParams,
                query: e.target.value,
              });
            }}
          />
        </label>

        <label htmlFor="start_date">
          From:
          <input
            type="text"
            id="start_date"
            placeholder="YYYY-MM-DD"
            value={requestParams.start_date}
            onChange={(e) =>
              setRequestParams({ ...requestParams, start_date: e.target.value })
            }
          />
        </label>

        <label htmlFor="end_date">
          To:
          <input
            type="text"
            id="end_date"
            placeholder="YYYY-MM-DD"
            value={requestParams.end_date}
            onChange={(e) =>
              setRequestParams({ ...requestParams, end_date: e.target.value })
            }
          />
        </label>

        <button type="submit" className="btn btn-default mt-4">
          Get
        </button>

        <button
          type="button"
          className="btn btn-default mt-4"
          onClick={() => setEditorState({})}
        >
          Add
        </button>
      </form>

      <div className="terminal-timeline">
        {observations.map((obs) => (
          <BrsnCard
            key={obs.id}
            obs={obs}
            deleteObservation={deleteObservation}
            onEdit={(state) => setEditorState(state)}
            globalHiddenImgs={globalHiddenImgs}
          />
        ))}
      </div>
    </div>
  );
}


interface IBrsnCardProps {
    obs: IObservation;
    deleteObservation: (id: number) => void;
    onEdit: (state: IEditorState) => void;
    globalHiddenImgs: boolean;
}

const BrsnCard = ({ obs, deleteObservation, onEdit, globalHiddenImgs }: IBrsnCardProps) => {
  const [hiddenImgs, setHiddenImgs] = React.useState<boolean>(true);

  const dateLabel = obs.date
    ? `${obs.date} ${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(obs.date + "T00:00:00").getDay()]} — `
    : "";

  return (
    <div key={obs.id} className="terminal-card">

      <header className="w-full flex justify-between" style={{padding: "10px 10px"}}>
        <div className="text-left">{dateLabel} {obs.description}</div>


        <div className="flex gap-6">
          <MdHideImage
            size={20}
            className="inline cursor-pointer"
            onClick={() => {
              setHiddenImgs(!hiddenImgs);
            }}
          />

          <MdEdit
            size={20}
            className="inline float-right cursor-pointer"
            onClick={() => {
              onEdit({
                id: obs.id,
                description: obs.description,
                content: obs.content,
                date: obs.date ?? "",
              });
            }}
          />

          <IoTrashBinSharp
            size={20}
            className="inline cursor-pointer"
            onClick={() => deleteObservation(obs.id)}
          />
        </div>
      </header>

      <div
        className={
          (hiddenImgs && globalHiddenImgs)
            ? "container-with-hidden-imgs"
            : ""
        }
        dangerouslySetInnerHTML={{ __html: obs.content }}
      />
    </div>
  );
};
